/**
 * Cliente de la API NOC (tag `noc` de `openapi.yaml`).
 *
 * Endpoints reales del backend (TG-92):
 * - GET /api/v1/noc/overview
 * - GET /api/v1/noc/stations
 * - GET /api/v1/noc/stations/:stationId
 * - GET /api/v1/noc/sessions/active
 *
 * Alertas y stream aún no existen (llegan con TG-93/94): se consultan de forma
 * defensiva y la UI cae a estado vacío / polling.
 */
import type {
  NocActiveSessionsResponse,
  NocAlert,
  NocAlertsResponse,
  NocOverview,
  NocStationDetail,
  NocStationsResponse,
} from '@goelectric/shared-types';
import { apiRequest } from '@/lib/http/apiClient';

export const nocApi = {
  overview(): Promise<NocOverview> {
    return apiRequest<NocOverview>('/api/v1/noc/overview', { retryOnUnauthorized: true });
  },

  stations(): Promise<NocStationsResponse> {
    return apiRequest<NocStationsResponse>('/api/v1/noc/stations', { retryOnUnauthorized: true });
  },

  station(stationId: string): Promise<NocStationDetail> {
    return apiRequest<NocStationDetail>(`/api/v1/noc/stations/${encodeURIComponent(stationId)}`);
  },

  activeSessions(): Promise<NocActiveSessionsResponse> {
    return apiRequest<NocActiveSessionsResponse>('/api/v1/noc/sessions/active', {
      retryOnUnauthorized: true,
    });
  },

  /** Alertas del NOC (filtrables por estado/estación/tipo). */
  alerts(status?: string): Promise<NocAlertsResponse> {
    const query = status ? `?status=${encodeURIComponent(status)}` : '';
    return apiRequest<NocAlertsResponse>(`/api/v1/noc/alerts${query}`, { retryOnUnauthorized: true });
  },

  /** Reconoce (ACK) una alerta abierta. */
  ackAlert(alertId: string): Promise<NocAlert> {
    return apiRequest<NocAlert>(`/api/v1/noc/alerts/${encodeURIComponent(alertId)}/ack`, {
      method: 'POST',
      retryOnUnauthorized: true,
    });
  },
};

/**
 * ¿Está habilitado el intento de SSE? Si el stream no existe, la UI usa polling.
 * Se activa con `VITE_NOC_SSE_ENABLED=true` (ver ADR 0027).
 */
export const NOC_SSE_ENABLED = import.meta.env.VITE_NOC_SSE_ENABLED === 'true';
